import { Injectable } from '@angular/core';

export interface CachedCatalog<T = unknown> {
  show: string;
  data: T;
  cachedAt: number;
}

/**
 * Persists the entry-less `GetShowDivisions` catalogue per show, so a reload
 * reads it back instead of refetching ~400 KB.
 *
 * Its own database rather than a store in `TLS-SA`: `IndexedDbService` injects
 * the catalogue service, and a second connection asking `TLS-SA` for another
 * version would block. Every failure degrades to a miss — it is only a cache.
 */
@Injectable({ providedIn: 'root' })
export class ShowCatalogCacheStore {
  private readonly dbName = 'TLS-SA-catalog';
  private readonly dbVersion = 1;
  private readonly storeName = 'showCatalog';

  private db?: Promise<IDBDatabase | null>;

  get<T>(show: string): Promise<CachedCatalog<T> | undefined> {
    return this.run<CachedCatalog<T> | undefined>('readonly', (store) => store.get(show), undefined);
  }

  put<T>(show: string, data: T): Promise<void> {
    const row: CachedCatalog<T> = { show, data, cachedAt: Date.now() };
    return this.run('readwrite', (store) => store.put(row), undefined).then(() => undefined);
  }

  delete(show: string): Promise<void> {
    return this.run('readwrite', (store) => store.delete(show), undefined).then(() => undefined);
  }

  clear(): Promise<void> {
    return this.run('readwrite', (store) => store.clear(), undefined).then(() => undefined);
  }

  private run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest,
    fallback: T
  ): Promise<T> {
    return this.open().then(
      (db) =>
        new Promise<T>((resolve) => {
          if (!db) {
            resolve(fallback);
            return;
          }
          try {
            const request = action(db.transaction(this.storeName, mode).objectStore(this.storeName));
            request.onsuccess = () => resolve((request.result as T) ?? fallback);
            request.onerror = () => resolve(fallback);
          } catch {
            resolve(fallback);
          }
        })
    );
  }

  private open(): Promise<IDBDatabase | null> {
    this.db ??= new Promise((resolve) => {
      try {
        const request = indexedDB.open(this.dbName, this.dbVersion);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains(this.storeName)) {
            request.result.createObjectStore(this.storeName, { keyPath: 'show' });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return this.db;
  }
}
